import { Fragment, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import clsx from 'clsx'
import { navigate } from 'gatsby'
import { isEqual, isEmpty } from 'lodash'
import { Badge, Button, Input, List, ListItem } from 'chaoskit/src/components'
import useUpdateEffect from 'react-use/lib/useUpdateEffect'
import {
  InstantSearch,
  Configure,
  Highlight,
  connectAutoComplete,
} from 'react-instantsearch-dom'
import { form, text } from 'chaoskit/src/assets/styles/utility'
import { generateGradient } from 'chaoskit/src/assets/styles/utility/gradient'

import { getAlgoliaIndexName } from '../../../utils/helpers'
import { NoResults } from '../search/algolia-global'

import { StylesDocsLayout } from './UI'

import Icon from '~components/Icon'
import Link from '~components/Link'
import useDebounce from '~hooks/useDebounce'
import useAlgoliaSearchClient from '~hooks/useAlgoliaSearchClient'

const SearchResults = ({ hits, activeHit, resetActiveHit }) => {
  const theme = useTheme()

  const searchResultsContainerRef = useRef()
  const activeHitRef = useRef()

  useUpdateEffect(() => {
    if (searchResultsContainerRef.current && activeHitRef.current) {
      // Update scroll position in overflow container to `activeHit` offset - the height of our section headers (since they are sticky now)
      searchResultsContainerRef.current.scrollTo({
        top: activeHitRef.current.offsetTop - theme.height['2xsmall'],
        behavior: 'smooth',
      })
    } else if (searchResultsContainerRef.current && !activeHitRef.current) {
      // Push scroll position to the top only if we're not already there (from a previously registered activeHit)
      if (searchResultsContainerRef.current.offsetTop > 0) {
        searchResultsContainerRef.current.scrollTo({ top: 0 })
      }
    }
  }, [activeHit])

  return (
    <div
      className={clsx(theme.settings.classes.trim, {
        'has-results': hits.length,
      })}
      css={{
        position: 'absolute',
        top: `calc(100% + ${theme.space.base}px)`,
        background: theme.color.light.base,
        borderRadius: theme.borderRadius.base,
        boxShadow: theme.boxShadow.large,
        border: theme.border.base,
        width: '100%',
        minHeight: 100,
        maxHeight: 400,
        maxWidth: StylesDocsLayout(theme).search.maxWidth,
        right: 0,
        overflow: 'auto',
        overscrollBehavior: 'contain',
        padding: theme.space.medium,
        zIndex: 10,
        WebkitOverflowScrolling: 'touch',

        '&.has-results': {
          padding: 0,
        },

        [theme.mq.medium]: {
          left: theme.space.base,
        },
      }}
      ref={searchResultsContainerRef}
    >
      {hits.length ? (
        <List
          css={{
            marginBottom: 0,
            borderRadius: theme.borderRadius.base,
          }}
        >
          <ListItem
            className={theme.settings.classes.contrast}
            css={{
              background: theme.color.dark.base,
              ...theme.text.xsmall,
              ...text.expanded(theme),
              fontWeight: theme.fontWeight.bold,
              height: theme.height['2xsmall'],
              position: 'sticky',
              top: 0,
              zIndex: 3,
              display: 'grid',
              placeContent: 'center',
            }}
          >
            Search results
          </ListItem>
          {hits.map((hit) => (
            <ListItem
              key={hit.slug}
              ref={isEqual(hit, activeHit) ? activeHitRef : null}
            >
              <Link
                to={`${hit.slug}${hit.contentId ? `#${hit.contentId}` : ''}`}
                css={[
                  {
                    display: 'block',
                    padding: theme.space.small,
                    position: 'relative',
                    overflow: 'hidden',
                    borderTop: theme.border.base,

                    '&:hover, &:focus, &.is-highlighted': {
                      background: generateGradient({
                        start: theme.color.panel.base,
                        stop: theme.color.panel.dark,
                        position: 'to bottom',
                      }),

                      '.Docs__SearchResults__Icon': {
                        opacity: 1,
                        transform: 'translateX(0)',
                      },
                    },
                  },
                ]}
                className={clsx({
                  'is-highlighted': isEqual(hit, activeHit),
                })}
                onClick={() => resetActiveHit()}
              >
                <div
                  css={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: theme.space.small,
                  }}
                >
                  <div
                    css={{
                      ...theme.text.medium,
                      fontWeight: theme.fontWeight.bold,
                    }}
                  >
                    <Highlight tagName="mark" attribute="title" hit={hit} />
                    <Icon
                      size="small"
                      css={{
                        opacity: 0,
                        transform: `translateX(-${theme.space.xsmall}px)`,
                        marginLeft: theme.space.small,
                        transition: `opacity ${theme.timing.base} ${theme.transition.bounce}, transform ${theme.timing.base} ${theme.transition.bounce}`,
                      }}
                      className="Docs__SearchResults__Icon"
                      icon="arrow-right"
                    />
                  </div>

                  <Badge type="secondary">{hit.section}</Badge>
                </div>
                {hit.excerpt && (
                  <p
                    css={{
                      marginTop: theme.space.small,
                      ...theme.text.small,
                      color: theme.fontColor.base,
                    }}
                  >
                    <Highlight tagName="mark" attribute="excerpt" hit={hit} />
                  </p>
                )}
              </Link>
            </ListItem>
          ))}
        </List>
      ) : (
        <NoResults />
      )}
    </div>
  )
}

SearchResults.propTypes = {
  hits: PropTypes.array,
  activeHit: PropTypes.object,
  resetActiveHit: PropTypes.func.isRequired,
}

const AutoComplete = ({ hits, currentRefinement, refine }) => {
  const theme = useTheme()

  const [searchValue, setSearchValue] = useState(currentRefinement)
  const [activeHit, setActiveHit] = useState({})

  // Debounce input changes
  const debouncedSearchValue = useDebounce(searchValue, 150)

  useUpdateEffect(() => {
    refine(debouncedSearchValue)
    // Always reset active hit so there's no persistence between new results
    setActiveHit({})
  }, [debouncedSearchValue])

  const handleKeyUp = ({ keyCode }) => {
    const up = 38
    const down = 40
    const enter = 13

    // If we have hits and we pressed one of our allowed keyCodes:
    if (hits.length && [up, down, enter].includes(keyCode)) {
      if (keyCode === down && !isEmpty(activeHit)) {
        // Find current active hit
        const position = hits.findIndex((hit) => hit.slug === activeHit.slug)

        // Set position to either the next one (if possible) or the beginning of the list
        setActiveHit(hits[position + 1] || hits[0])
      } else if (keyCode === down) {
        setActiveHit(hits[0])
      } else if (keyCode === up && !isEmpty(activeHit)) {
        // Find current active hit
        const position = hits.findIndex((hit) => hit.slug === activeHit.slug)

        // Set position to either the previous one (if possible) or last of the list
        setActiveHit(hits[position - 1] || hits[hits.length - 1])
      } else if (keyCode === up) {
        setActiveHit(hits[hits.length - 1])
      } else if (keyCode === enter && !isEmpty(activeHit)) {
        // Reset between clicks
        refine('')
        setSearchValue('')

        navigate(activeHit.slug)
      }
    }
  }

  return (
    <Fragment>
      <Input
        autoComplete="off"
        name="search"
        label="Search documentation"
        placeholder="Installation"
        value={searchValue}
        onChange={({ target: { value } }) => setSearchValue(value)}
        className={clsx({ 'has-query': searchValue.length !== 0 })}
        onKeyUp={handleKeyUp}
      />
      {searchValue.length !== 0 && (
        <Button
          size="xsmall"
          onClick={() => {
            setSearchValue('')
            refine('')
          }}
          iconOnly
          title="Reset query"
          css={{
            position: 'absolute',
            right: form.variables(theme).controlOffset,
            zIndex: 2,
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        >
          <Icon icon="close" />
        </Button>
      )}

      {currentRefinement && (
        <SearchResults
          hits={hits}
          activeHit={activeHit}
          resetActiveHit={() => refine('')}
        />
      )}
    </Fragment>
  )
}

const CustomAutoComplete = connectAutoComplete(AutoComplete)

AutoComplete.propTypes = {
  hits: PropTypes.array,
  currentRefinement: PropTypes.string,
  refine: PropTypes.func,
}

const { algoliaIndex } = getAlgoliaIndexName()

const DocSearch = () => {
  const theme = useTheme()

  const searchClient = useAlgoliaSearchClient((state) => state.searchClient)

  return (
    <Fragment>
      <div
        css={{
          flex: 1,
          maxWidth: StylesDocsLayout(theme).search.maxWidth,
          marginRight: theme.space.base,
          position: 'relative',

          '.CK__ControlWrapper': {
            background: 'transparent',
            border: 0,
            boxShadow: 'none',

            // Add padding to input so we can display reset button
            '&.has-query input': {
              paddingRight:
                form.variables(theme).controlOffset +
                theme.fontSize.base +
                theme.space.small,
            },
          },
        }}
      >
        <InstantSearch indexName={algoliaIndex} searchClient={searchClient}>
          <CustomAutoComplete />
          <Configure filters="articleLabel:Docs" hitsPerPage={20} />
        </InstantSearch>
      </div>
    </Fragment>
  )
}

export default DocSearch
